import { msg } from 'gt-react';
import { createRoute } from '@tanstack/react-router';
import { ShieldCheckIcon } from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import { orpc } from '@/api/rpc/client';
import { Route as pathlessAuthenticatedRoute } from '../../route';
import { ValidationsPage } from './ValidationsPage';
import { ValidationRuleDetailPage } from './ValidationRuleDetailPage';

/** The validation rules list. Renders `<Outlet />` when a rule is open. */
export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/item-master/validations',
  staticData: {
    app: {
      nav: {
        label: msg('Validations', {
          context: 'sidebar nav label, Item Master section',
        }),
        icon: <ShieldCheckIcon className="h-4 w-4" weight="fill" />,
        group: 'Item Master',
        order: 25,
        groupOrder: 27,
      },
      access: {
        featureFlag: 'item-master-ui-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
    },
  },
  component: ValidationsPage,
  loader: () => ({
    crumb: msg('Validations', { context: 'page breadcrumb' }),
  }),
});

/**
 * One rule's detail page. Declared here rather than in its own
 * `$ruleId.tsx` module because a child route in a separate file cannot state
 * its parent's path segments, which `custom/tanstack-route-file-path` checks
 * the file location against.
 */
export const ValidationRuleDetailRoute = createRoute({
  getParentRoute: () => Route,
  path: '$ruleId',
  staticData: {
    app: {
      access: {
        featureFlag: 'item-master-ui-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
    },
  },
  component: ValidationRuleDetailPage,
  loader: async ({ params, context }) => {
    // Fetch the rule name for the breadcrumb, cached via queryClient.
    try {
      const data = await context.queryClient.fetchQuery(
        orpc.inventoryManagement.validationRules.get.queryOptions({
          input: { id: params.ruleId },
        }),
      );
      return {
        crumb:
          data.rule?.name ??
          msg('Validation rule', { context: 'page breadcrumb' }),
      };
    } catch {
      return { crumb: msg('Validation rule', { context: 'page breadcrumb' }) };
    }
  },
});
